/**
 * AdvertiserManagerPage — Trackier-style advertiser manager CRUD list.
 * Topbar + filter row + table + create drawer (zod+RHF).
 * Green accent on white/zinc-200/zinc-900, Inter, 6-8px rounding.
 */
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
	useAdvertiserManagerList,
	useAdvertiserManagerCreate,
	useAdvertiserManagerDelete,
} from "./hooks";
import type { AdvertiserManagerRow, AdvertiserStatus } from "./api";

const STATUS_OPTIONS: ReadonlyArray<{ value: "all" | AdvertiserStatus; label: string }> = [
	{ value: "all", label: "All status" },
	{ value: "active", label: "Active" },
	{ value: "pending", label: "Pending" },
	{ value: "paused", label: "Paused" },
	{ value: "archived", label: "Archived" },
];

const VERTICAL_OPTIONS: ReadonlyArray<string> = [
	"E-commerce",
	"Finance",
	"Gaming",
	"Travel",
	"Health",
	"Education",
	"Subscription",
	"Other",
];

const createSchema = z.object({
	name: z.string().trim().min(2, "Name must be at least 2 characters"),
	contact_email: z.string().trim().email("Enter a valid email address"),
	vertical: z.string().trim().min(1, "Choose a vertical"),
	payment_terms: z.string().trim().min(1, "Choose payment terms"),
});

type CreateFormValues = z.infer<typeof createSchema>;

const STATUS_BADGE: Record<AdvertiserStatus, string> = {
	active: "bg-green-50 text-green-700 ring-green-600/20",
	pending: "bg-amber-50 text-amber-700 ring-amber-600/20",
	paused: "bg-zinc-100 text-zinc-700 ring-zinc-500/20",
	archived: "bg-zinc-100 text-zinc-500 ring-zinc-400/20",
};

function StatusBadge({ status }: { status: AdvertiserStatus }) {
	return (
		<span
			className={`inline-flex items-center rounded-[6px] px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${STATUS_BADGE[status]}`}
			data-testid={`advertiser-manager-status-${status}`}
		>
			{status.charAt(0).toUpperCase() + status.slice(1)}
		</span>
	);
}

function SearchIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="h-4 w-4 text-zinc-400"
			aria-hidden="true"
		>
			<circle cx="9" cy="9" r="6" />
			<path d="m17 17-3.5-3.5" />
		</svg>
	);
}

function PlusIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="h-4 w-4"
			aria-hidden="true"
		>
			<path d="M10 4v12" />
			<path d="M4 10h12" />
		</svg>
	);
}

function CloseIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="h-4 w-4"
			aria-hidden="true"
		>
			<path d="m5 5 10 10" />
			<path d="m15 5-10 10" />
		</svg>
	);
}

function AlertIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="h-4 w-4 text-red-600"
			aria-hidden="true"
		>
			<path d="M10 3 2 17h16z" />
			<path d="M10 8v4" />
			<path d="M10 15v.01" />
		</svg>
	);
}

function normalizeRow(row: AdvertiserManagerRow) {
	const status: AdvertiserStatus =
		row.status === "active" ||
		row.status === "paused" ||
		row.status === "pending" ||
		row.status === "archived"
			? row.status
			: "pending";
	return {
		id: row.id,
		name: row.name ?? "Untitled advertiser",
		contact_email: row.contact_email ?? "",
		vertical: row.vertical ?? "",
		campaigns_count: typeof row.campaigns_count === "number" ? row.campaigns_count : 0,
		pending_approvals:
			typeof row.pending_approvals === "number" ? row.pending_approvals : 0,
		status,
	};
}

export function AdvertiserManagerPage() {
	const listQuery = useAdvertiserManagerList();
	const createMut = useAdvertiserManagerCreate();
	const deleteMut = useAdvertiserManagerDelete();

	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<"all" | AdvertiserStatus>("all");
	const [verticalFilter, setVerticalFilter] = useState<string>("all");
	const [drawerOpen, setDrawerOpen] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<CreateFormValues>({
		resolver: zodResolver(createSchema),
		defaultValues: {
			name: "",
			contact_email: "",
			vertical: "",
			payment_terms: "Net 30",
		},
	});

	const rawItems = listQuery.data?.items ?? [];
	const items = useMemo(() => rawItems.map(normalizeRow), [rawItems]);
	const verticals = useMemo(() => {
		const s = new Set<string>();
		for (const it of items) if (it.vertical) s.add(it.vertical);
		return Array.from(s).sort();
	}, [items]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return items.filter((row) => {
			if (statusFilter !== "all" && row.status !== statusFilter) return false;
			if (verticalFilter !== "all" && row.vertical !== verticalFilter) return false;
			if (!q) return true;
			return (
				row.name.toLowerCase().includes(q) ||
				row.contact_email.toLowerCase().includes(q) ||
				row.vertical.toLowerCase().includes(q)
			);
		});
	}, [items, search, statusFilter, verticalFilter]);

	const closeDrawer = () => {
		setDrawerOpen(false);
		reset();
	};

	const onCreateSubmit = handleSubmit((values) => {
		createMut.mutate(values, {
			onSuccess: () => {
				reset();
				setDrawerOpen(false);
			},
		});
	});

	const busyCreate = createMut.isPending || isSubmitting;
	const listError = listQuery.error as Error | null | undefined;

	return (
		<main
			data-testid="advertiser-manager-page"
			className="min-h-screen bg-zinc-50 font-[Inter,ui-sans-serif,system-ui] text-zinc-900"
		>
			<div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8">
				<header
					className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between"
					data-testid="advertiser-manager-topbar"
				>
					<div className="flex flex-col gap-1">
						<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-500">
							Advertisers
						</p>
						<h1 className="text-[22px] font-semibold leading-tight text-zinc-900">
							Advertiser Manager
						</h1>
						<p className="text-sm text-zinc-500">
							Review, approve, and manage advertisers running across your network.
						</p>
					</div>
					<button
						type="button"
						onClick={() => setDrawerOpen(true)}
						className="inline-flex items-center gap-1.5 self-start rounded-[6px] border border-green-700 bg-green-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 sm:self-auto"
						data-testid="advertiser-manager-create-open"
					>
						<PlusIcon />
						Create Advertiser
					</button>
				</header>

				<section
					className="flex flex-col gap-3 rounded-[8px] border border-zinc-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center"
					data-testid="advertiser-manager-filters"
				>
					<label className="relative flex-1">
						<span className="sr-only">Search advertisers</span>
						<span className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
							<SearchIcon />
						</span>
						<input
							type="search"
							value={search}
							onChange={(e) => setSearch(e.currentTarget.value)}
							placeholder="Search by name, email, or vertical"
							className="w-full rounded-[6px] border border-zinc-200 bg-white py-2 pl-9 pr-3 text-sm placeholder-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
							data-testid="advertiser-manager-search"
						/>
					</label>
					<select
						value={statusFilter}
						onChange={(e) =>
							setStatusFilter(e.currentTarget.value as "all" | AdvertiserStatus)
						}
						className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
						data-testid="advertiser-manager-status-filter"
						aria-label="Filter by status"
					>
						{STATUS_OPTIONS.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
					<select
						value={verticalFilter}
						onChange={(e) => setVerticalFilter(e.currentTarget.value)}
						className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
						data-testid="advertiser-manager-vertical-filter"
						aria-label="Filter by vertical"
					>
						<option value="all">All verticals</option>
						{verticals.map((v) => (
							<option key={v} value={v}>
								{v}
							</option>
						))}
					</select>
				</section>

				<section
					className="overflow-hidden rounded-[8px] border border-zinc-200 bg-white shadow-sm"
					aria-label="Advertisers list"
				>
					<div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
						<h2 className="text-sm font-semibold text-zinc-900">Advertisers</h2>
						<span className="text-xs text-zinc-500" data-testid="advertiser-manager-count">
							{filtered.length} {filtered.length === 1 ? "advertiser" : "advertisers"}
						</span>
					</div>

					{listQuery.isLoading ? (
						<div
							className="flex items-center gap-3 px-4 py-10 text-sm text-zinc-500"
							data-testid="advertiser-manager-loading"
						>
							<span
								className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-green-600 border-t-transparent"
								aria-hidden="true"
							/>
							Loading advertisers
						</div>
					) : listError ? (
						<div
							className="flex items-start gap-3 px-4 py-8 text-sm text-red-700"
							role="alert"
							data-testid="advertiser-manager-error"
						>
							<AlertIcon />
							<div>
								<p className="font-medium">Couldn't load advertisers</p>
								<p className="text-red-600/80">{listError.message}</p>
							</div>
						</div>
					) : filtered.length === 0 ? (
						<div
							className="flex flex-col items-start gap-2 px-4 py-12 text-sm text-zinc-500"
							data-testid="advertiser-manager-empty"
						>
							<p className="font-medium text-zinc-700">No advertisers yet</p>
							<p>
								Create your first advertiser to start tracking campaigns and approvals.
							</p>
							<button
								type="button"
								onClick={() => setDrawerOpen(true)}
								className="mt-2 inline-flex items-center gap-1.5 rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
								data-testid="advertiser-manager-empty-create"
							>
								<PlusIcon />
								Create Advertiser
							</button>
						</div>
					) : (
						<div className="overflow-x-auto">
							<table
								className="min-w-full divide-y divide-zinc-200 text-left text-sm"
								data-testid="advertiser-manager-table"
							>
								<thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
									<tr>
										<th scope="col" className="px-4 py-2.5 font-medium">Name</th>
										<th scope="col" className="px-4 py-2.5 font-medium">Contact</th>
										<th scope="col" className="px-4 py-2.5 font-medium">Vertical</th>
										<th scope="col" className="px-4 py-2.5 font-medium text-right">Campaigns</th>
										<th scope="col" className="px-4 py-2.5 font-medium text-right">Pending</th>
										<th scope="col" className="px-4 py-2.5 font-medium">Status</th>
										<th scope="col" className="px-4 py-2.5 font-medium text-right">Actions</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-zinc-100 bg-white">
									{filtered.map((row) => (
										<tr
											key={row.id}
											className="hover:bg-zinc-50"
											data-testid={`advertiser-manager-row-${row.id}`}
										>
											<td className="px-4 py-3">
												<div className="font-medium text-zinc-900">{row.name}</div>
												<div className="text-[11px] font-mono text-zinc-400">{row.id}</div>
											</td>
											<td className="px-4 py-3 text-zinc-700">{row.contact_email || "\u2014"}</td>
											<td className="px-4 py-3 text-zinc-700">{row.vertical || "\u2014"}</td>
											<td className="px-4 py-3 text-right tabular-nums text-zinc-700">
												{row.campaigns_count}
											</td>
											<td className="px-4 py-3 text-right tabular-nums">
												{row.pending_approvals > 0 ? (
													<span className="inline-flex items-center rounded-[6px] bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20">
														{row.pending_approvals}
													</span>
												) : (
													<span className="text-zinc-400">0</span>
												)}
											</td>
											<td className="px-4 py-3">
												<StatusBadge status={row.status} />
											</td>
											<td className="px-4 py-3 text-right">
												<button
													type="button"
													onClick={() => deleteMut.mutate(row.id)}
													disabled={deleteMut.isPending}
													className="rounded-[6px] border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
													data-testid={`advertiser-manager-delete-${row.id}`}
												>
													Delete
												</button>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</section>
			</div>

			{drawerOpen ? (
				<div
					className="fixed inset-0 z-40 flex justify-end bg-zinc-900/40"
					data-testid="advertiser-manager-drawer"
					role="dialog"
					aria-modal="true"
					aria-labelledby="advertiser-manager-drawer-title"
				>
					<button
						type="button"
						aria-label="Close drawer backdrop"
						onClick={closeDrawer}
						className="flex-1 cursor-default"
					/>
					<aside className="flex h-full w-full max-w-md flex-col border-l border-zinc-200 bg-white shadow-xl">
						<div className="flex items-start justify-between border-b border-zinc-200 px-5 py-4">
							<div>
								<p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-green-700">
									New advertiser
								</p>
								<h3
									id="advertiser-manager-drawer-title"
									className="text-base font-semibold text-zinc-900"
								>
									Create Advertiser
								</h3>
							</div>
							<button
								type="button"
								onClick={closeDrawer}
								className="rounded-[6px] p-1 text-zinc-500 hover:bg-zinc-100"
								aria-label="Close drawer"
								data-testid="advertiser-manager-drawer-close"
							>
								<CloseIcon />
							</button>
						</div>

						<form
							onSubmit={onCreateSubmit}
							className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5 text-sm"
							data-testid="advertiser-manager-create-form"
							noValidate
						>
							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-zinc-700">Name</span>
								<input
									type="text"
									className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
									placeholder="Acme Retail"
									aria-invalid={errors.name ? "true" : "false"}
									data-testid="advertiser-manager-field-name"
									{...register("name")}
								/>
								{errors.name ? (
									<span
										className="text-xs text-red-600"
										role="alert"
										data-testid="advertiser-manager-error-name"
									>
										{errors.name.message}
									</span>
								) : null}
							</label>

							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-zinc-700">Contact email</span>
								<input
									type="email"
									className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
									placeholder="ops@acme.com"
									aria-invalid={errors.contact_email ? "true" : "false"}
									data-testid="advertiser-manager-field-contact"
									{...register("contact_email")}
								/>
								{errors.contact_email ? (
									<span
										className="text-xs text-red-600"
										role="alert"
										data-testid="advertiser-manager-error-contact"
									>
										{errors.contact_email.message}
									</span>
								) : null}
							</label>

							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-zinc-700">Vertical</span>
								<select
									className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
									aria-invalid={errors.vertical ? "true" : "false"}
									data-testid="advertiser-manager-field-vertical"
									{...register("vertical")}
								>
									<option value="">Select a vertical</option>
									{VERTICAL_OPTIONS.map((v) => (
										<option key={v} value={v}>
											{v}
										</option>
									))}
								</select>
								{errors.vertical ? (
									<span
										className="text-xs text-red-600"
										role="alert"
										data-testid="advertiser-manager-error-vertical"
									>
										{errors.vertical.message}
									</span>
								) : null}
							</label>

							<label className="flex flex-col gap-1">
								<span className="text-xs font-medium text-zinc-700">Payment terms</span>
								<select
									className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20"
									aria-invalid={errors.payment_terms ? "true" : "false"}
									data-testid="advertiser-manager-field-terms"
									{...register("payment_terms")}
								>
									<option value="Net 15">Net 15</option>
									<option value="Net 30">Net 30</option>
									<option value="Net 45">Net 45</option>
									<option value="Net 60">Net 60</option>
									<option value="Prepaid">Prepaid</option>
								</select>
								{errors.payment_terms ? (
									<span
										className="text-xs text-red-600"
										role="alert"
										data-testid="advertiser-manager-error-terms"
									>
										{errors.payment_terms.message}
									</span>
								) : null}
							</label>

							{createMut.error ? (
								<p
									className="rounded-[6px] bg-red-50 px-3 py-2 text-xs text-red-700 ring-1 ring-inset ring-red-600/20"
									role="alert"
									data-testid="advertiser-manager-submit-error"
								>
									{createMut.error.message}
								</p>
							) : null}

							<div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
								<button
									type="button"
									onClick={closeDrawer}
									className="rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
									data-testid="advertiser-manager-cancel"
								>
									Cancel
								</button>
								<button
									type="submit"
									disabled={busyCreate}
									className="inline-flex items-center gap-1.5 rounded-[6px] border border-green-700 bg-green-600 px-3.5 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60"
									data-testid="advertiser-manager-submit"
								>
									{busyCreate ? "Saving" : "Create advertiser"}
								</button>
							</div>
						</form>
					</aside>
				</div>
			) : null}
		</main>
	);
}

export default AdvertiserManagerPage;
